import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { submissionsAPI, driversAPI, templatesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  ArrowLeft,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Clock,
  FileText,
  User,
  Truck,
  Calendar,
  AlertCircle,
  Loader2,
  Download,
  Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const statusConfig = {
  draft: { label: 'Draft', icon: FileText, className: 'badge-neutral', color: 'slate' },
  submitted: { label: 'Pending Approval', icon: Clock, className: 'badge-warning', color: 'amber' },
  returned: { label: 'Returned', icon: AlertCircle, className: 'badge-info', color: 'blue' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'badge-success', color: 'emerald' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'badge-error', color: 'red' },
  closed: { label: 'Closed', icon: CheckCircle2, className: 'badge-neutral', color: 'slate' },
};

export const SubmissionDetailPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user, isSupervisor } = useAuth();
  
  const [submission, setSubmission] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [templateFields, setTemplateFields] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [returnReason, setReturnReason] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [submissionRes, driversRes] = await Promise.all([
          submissionsAPI.get(submissionId),
          driversAPI.list()
        ]);
        setSubmission(submissionRes.data);
        setDrivers(driversRes.data || []);
        // Fetch template fields for section mapping
        const tCode = submissionRes.data?.template_code;
        if (tCode) {
          try {
            const tplRes = await templatesAPI.get(tCode);
            setTemplateFields(tplRes.data?.fields || []);
          } catch {}
        }
      } catch (error) {
        console.error('Failed to fetch submission:', error);
        toast.error('Failed to load submission');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [submissionId]);

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await submissionsAPI.approve(submissionId);
      toast.success('Submission approved');
      // Refresh submission
      const response = await submissionsAPI.get(submissionId);
      setSubmission(response.data);
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error('Failed to approve submission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setActionLoading(true);
      await submissionsAPI.reject(submissionId, rejectReason);
      toast.success('Submission rejected');
      setShowRejectDialog(false);
      // Refresh submission
      const response = await submissionsAPI.get(submissionId);
      setSubmission(response.data);
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject submission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReturn = async () => {
    if (!returnReason.trim()) {
      toast.error('Please provide a reason for return');
      return;
    }
    
    try {
      setActionLoading(true);
      await submissionsAPI.return(submissionId, returnReason);
      toast.success('Submission returned for corrections');
      setShowReturnDialog(false);
      // Refresh submission
      const response = await submissionsAPI.get(submissionId);
      setSubmission(response.data);
    } catch (error) {
      console.error('Failed to return:', error);
      toast.error('Failed to return submission');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-700">Submission not found</h3>
        <Button onClick={() => navigate('/submissions')} className="mt-4">
          Back to Submissions
        </Button>
      </div>
    );
  }

  // Render form data organized by sections
  const renderFormData = (formData, templateCode) => {
    // Helper to get driver name from ID
    const getDriverName = (driverId) => {
      const driver = drivers.find(d => d.id === driverId);
      if (driver) {
        const fn = driver.first_name || driver.user?.first_name || '';
        const ln = driver.last_name || driver.user?.last_name || '';
        return `${fn} ${ln}`.trim() || driver.employee_number || driverId;
      }
      return driverId;
    };

    // Helper to format value
    const formatValue = (key, value) => {
      if (key === 'driver_name' && value) return getDriverName(value);
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (value === 'none' || value === null || value === undefined) return '-';
      return String(value || '-');
    };

    if (templateCode !== 'F004' || !templateFields || templateFields.length === 0) {
      // Non-inspection forms or no template data - render simply
      return Object.entries(formData).map(([key, value]) => {
        if (key.endsWith('_comment') || key.startsWith('section_')) return null;
        return (
          <div key={key} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
            <span className="text-slate-500 text-sm capitalize">{key.replace(/_/g, ' ')}</span>
            <span className="font-medium text-slate-900 text-right">{formatValue(key, value)}</span>
          </div>
        );
      });
    }

    // Build sections from template fields
    const sectionGroups = [];
    let currentSection = { title: 'General Information', fields: [] };

    for (const f of templateFields) {
      if (f.field_type === 'section') {
        if (currentSection.fields.length > 0) {
          sectionGroups.push(currentSection);
        }
        currentSection = { title: f.label, fields: [] };
      } else {
        currentSection.fields.push({
          name: f.name,
          label: f.label,
          type: f.field_type,
          expected_answer: f.expected_answer,
        });
      }
    }
    if (currentSection.fields.length > 0) {
      sectionGroups.push(currentSection);
    }

    return sectionGroups.map((section, si) => {
      const items = section.fields.filter(f => {
        const val = formData[f.name];
        return val !== undefined && val !== null;
      });
      if (items.length === 0) return null;

      return (
        <div key={si} className="mb-4">
          <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2 pb-1 border-b border-slate-200">
            {section.title}
          </h4>
          {items.map(f => {
            const value = formData[f.name];
            const comment = formData[`${f.name}_comment`];
            const isNeedsAttention = value === 'Needs Attention';
            // For yes/no: only flag as problem if answer contradicts expected_answer
            const isYesNoProblem = f.expected_answer && (
              (f.expected_answer === 'yes_required' && value === 'No') ||
              (f.expected_answer === 'no_required' && value === 'Yes')
            );
            const isProblem = isNeedsAttention || isYesNoProblem;

            return (
              <div key={f.name} className={`py-2 border-b border-slate-100 last:border-0 ${isProblem ? 'bg-red-50 px-2 rounded' : ''}`}>
                <div className="flex justify-between items-start">
                  <span className={`text-sm ${isProblem ? 'text-red-700 font-medium' : 'text-slate-500'}`}>
                    {f.label}
                  </span>
                  <span className={`font-medium text-right ${
                    isProblem ? 'text-red-600' :
                    value === 'In Order' || value === 'Yes' ? 'text-emerald-600' :
                    'text-slate-900'
                  }`}>
                    {formatValue(f.name, value)}
                  </span>
                </div>
                {comment && (
                  <div className="mt-1 text-sm text-red-600 italic pl-2 border-l-2 border-red-300">
                    Issue: {comment}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    });
  };

  const status = statusConfig[submission.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  const canApprove = isSupervisor && submission.status === 'submitted';

  return (
    <div className="space-y-6 pb-24 md:pb-6 animate-fade-in" data-testid="submission-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/submissions')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge variant="outline" className="font-mono text-sm">
              {submission.submission_number}
            </Badge>
            <Badge className={status.className}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
          </div>
          <h1 
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            {submission.template?.name || submission.template_code}
          </h1>
        </div>
        <div className="hidden md:flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submission.id}/export/excel`, { responseType: 'blob' });
                const disposition = res.headers['content-disposition'];
                let filename = `${submission.submission_number}.xlsx`;
                if (disposition) {
                  const match = disposition.match(/filename=([^;]+)/);
                  if (match) filename = match[1].replace(/"/g, '');
                }
                const blob = new Blob([res.data]);
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              } catch { toast.error('Export failed'); }
            }}
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={async () => {
              const newWindow = window.open('about:blank', '_blank');
              try {
                const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/${submission.id}/export/pdf`, { responseType: 'blob' });
                const blob = new Blob([res.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                if (newWindow) { newWindow.location.href = url; } else { window.open(url, '_blank'); }
              } catch { if (newWindow) newWindow.close(); toast.error('Export failed'); }
            }}
            data-testid="export-pdf-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Status Timeline */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between overflow-x-auto py-2">
            {['draft', 'submitted', 'approved'].map((step, index) => {
              const stepConfig = statusConfig[step];
              const StepIcon = stepConfig.icon;
              const isComplete = 
                (step === 'draft') ||
                (step === 'submitted' && ['submitted', 'approved', 'rejected', 'returned'].includes(submission.status)) ||
                (step === 'approved' && submission.status === 'approved');
              const isCurrent = submission.status === step;
              
              return (
                <React.Fragment key={step}>
                  <div className={`flex flex-col items-center gap-2 min-w-[80px] ${isCurrent ? 'opacity-100' : isComplete ? 'opacity-100' : 'opacity-40'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-emerald-100 text-emerald-600' :
                      isCurrent ? 'bg-amber-100 text-amber-600' :
                      'bg-slate-100 text-slate-400'
                    }`}>
                      <StepIcon className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-slate-600 text-center">
                      {stepConfig.label}
                    </span>
                  </div>
                  {index < 2 && (
                    <div className={`flex-1 h-0.5 mx-2 ${isComplete ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Return/Rejection Reason */}
      {(submission.return_reason || submission.rejection_reason) && (
        <Card className={`border-l-4 ${submission.rejection_reason ? 'border-l-red-500 bg-red-50' : 'border-l-blue-500 bg-blue-50'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className={`w-5 h-5 mt-0.5 ${submission.rejection_reason ? 'text-red-500' : 'text-blue-500'}`} />
              <div>
                <p className="font-semibold text-slate-900">
                  {submission.rejection_reason ? 'Rejection Reason' : 'Return Reason'}
                </p>
                <p className="text-slate-700 mt-1">
                  {submission.rejection_reason || submission.return_reason}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submission Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column - Assignment Info */}
        <Card>
          <CardHeader>
            <CardTitle className="form-section-title">Assignment Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {submission.vehicle && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Truck className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vehicle</p>
                  <p className="font-mono font-semibold text-slate-900">
                    {submission.vehicle.registration_number}
                  </p>
                </div>
              </div>
            )}
            
            {submission.driver?.user && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Driver</p>
                  <p className="font-semibold text-slate-900">
                    {submission.driver.user.first_name} {submission.driver.user.last_name}
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Created</p>
                <p className="font-semibold text-slate-900">
                  {new Date(submission.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {submission.approved_at && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="font-semibold text-slate-900">
                    {new Date(submission.approved_at).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Column - Form Data */}
        <Card>
          <CardHeader>
            <CardTitle className="form-section-title">Form Data</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(submission.form_data || {}).length > 0 ? (
              <div className="space-y-3">
                {renderFormData(submission.form_data, submission.template_code)}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No form data recorded</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Items / Inspection Results */}
      {submission.line_items?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="form-section-title">
              Inspection Results
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({submission.line_items.filter(i => i.status === 'OK').length}/{submission.line_items.length} OK)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {submission.line_items.map((item, index) => (
                <div 
                  key={item.id || index}
                  className={`flex items-center justify-between p-3 rounded-sm ${
                    item.status === 'OK' ? 'bg-emerald-50' :
                    item.status === 'Problem' ? 'bg-red-50' :
                    'bg-slate-50'
                  }`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{item.item_name}</p>
                    {item.comment && (
                      <p className="text-sm text-slate-600 mt-1">{item.comment}</p>
                    )}
                  </div>
                  <Badge className={
                    item.status === 'OK' ? 'badge-success' :
                    item.status === 'Problem' ? 'badge-error' :
                    'badge-neutral'
                  }>
                    {item.status || 'Not Checked'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signatures */}
      {(submission.driver_signature || submission.supervisor_signature) && (
        <Card>
          <CardHeader>
            <CardTitle className="form-section-title">Signatures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {submission.driver_signature && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Driver Signature</p>
                  <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-2">
                    <img 
                      src={submission.driver_signature} 
                      alt="Driver signature" 
                      className="max-h-24 mx-auto"
                    />
                  </div>
                  {submission.driver_signed_at && (
                    <p className="text-xs text-slate-500 mt-1">
                      Signed: {new Date(submission.driver_signed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              {submission.supervisor_signature && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">Supervisor Signature</p>
                  <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-2">
                    <img 
                      src={submission.supervisor_signature} 
                      alt="Supervisor signature" 
                      className="max-h-24 mx-auto"
                    />
                  </div>
                  {submission.supervisor_signed_at && (
                    <p className="text-xs text-slate-500 mt-1">
                      Signed: {new Date(submission.supervisor_signed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Actions */}
      {canApprove && (
        <div className="sticky-action-bar">
          <Button 
            variant="outline"
            onClick={() => setShowReturnDialog(true)}
            disabled={actionLoading}
            className="flex-1 md:flex-none h-12"
            data-testid="return-btn"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Return
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowRejectDialog(true)}
            disabled={actionLoading}
            className="flex-1 md:flex-none h-12 text-red-600 border-red-200 hover:bg-red-50"
            data-testid="reject-btn"
          >
            <XCircle className="w-5 h-5 mr-2" />
            Reject
          </Button>
          <Button 
            onClick={handleApprove}
            disabled={actionLoading}
            className="flex-1 md:flex-none h-12 bg-emerald-500 hover:bg-emerald-600"
            data-testid="approve-btn"
          >
            {actionLoading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-5 h-5 mr-2" />
            )}
            Approve
          </Button>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Submission</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this submission. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-reject-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return for Corrections</DialogTitle>
            <DialogDescription>
              Please specify what corrections are needed. The submitter will be notified.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter required corrections..."
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value)}
            rows={4}
            data-testid="return-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReturn}
              disabled={actionLoading}
              data-testid="confirm-return-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmissionDetailPage;
